import { LocalStorageKey } from 'shared/constants/localStorage'

import type { LoggerService } from '../LoggerService'
import { getAuthStorageReason } from '../utils/authDiagnostics'
import { summarizeUserStorageValue } from '../utils/tokenInfo'

const isAuthKey = (key: string) =>
  key === LocalStorageKey.USER || key === LocalStorageKey.BOOTSTRAP_TOKENS

// Keys and patterns to filter out entirely — never log events for these
const FILTERED_KEY_PATTERNS = new Set<string>([
  'disabled_rich_app_icons',
  'AMP_*',
  'clusterix-task-management-ls-key*',
  '*_store',
  'pusher*',
  '*access_token',
  '*access-token',
  '*_token',
  '@timetracking/*',
  'timetracking/*',
  'debug',
  'jam_*',
  'notificationViewType',
  'firebase*',
  'isEmbeddedInAfterSales*',
  'columnsState*',
  'group_tasks_by_kanban',
  '__some_random_key__',
  'i18next.*'
])

function matchesPattern(key: string, pattern: string): boolean {
  // Handle wildcard patterns (e.g., 'AMP_*')
  if (pattern.includes('*')) {
    const regexPattern = pattern
      .split('*')
      .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('.*')
    const regex = new RegExp(`^${regexPattern}$`)
    return regex.test(key)
  }
  return key === pattern
}

function shouldFilterKey(key: string): boolean {
  return Array.from(FILTERED_KEY_PATTERNS).some(pattern =>
    matchesPattern(key, pattern)
  )
}

// Values for these keys are always fully redacted
const REDACTED_KEYS = new Set<string>([
  LocalStorageKey.ACCESS_TOKEN,
  LocalStorageKey.BOOTSTRAP_TOKENS,
  LocalStorageKey.USER,
  LocalStorageKey.SELECTABLE_USERS
])

// Stack traces are only captured for these keys — they're auth/session
// critical and their writes are infrequent, so the cost is negligible.
// All other keys skip stack capture entirely to avoid main-thread stalls.
const STACK_TRACE_KEYS = new Set<string>([
  LocalStorageKey.ACCESS_TOKEN,
  LocalStorageKey.BOOTSTRAP_TOKENS,
  LocalStorageKey.USER,
  LocalStorageKey.SELECTABLE_USERS,
  'i18nextLng'
])

// If a value contains any of these keywords, the whole value is redacted
const SENSITIVE_VALUE_KEYWORDS = [
  'token',
  'secret',
  'password',
  'passwd',
  'credential',
  'api_key',
  'apikey',
  'authorization',
  'private_key'
]

// Matches a JWT: header.payload.signature — all three parts base64url-encoded
const JWT_REGEX = /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/

function containsJwt(value: string): boolean {
  return JWT_REGEX.test(value)
}

function isEmptyish(value: string): boolean {
  if (value === '') return true
  try {
    const parsed = JSON.parse(value)
    if (parsed === null || parsed === undefined) return true
    if (
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Object.keys(parsed).length === 0
    )
      return true
  } catch {
    // not JSON — not emptyish
  }
  return false
}

function maybeRedact(key: string, value: string | null): string | null {
  const REDACTED_STR = '[REDACTED]'

  if (value === null) return null
  if (REDACTED_KEYS.has(key)) return isEmptyish(value) ? value : REDACTED_STR
  const lower = value.toLowerCase()
  if (SENSITIVE_VALUE_KEYWORDS.some(kw => lower.includes(kw)))
    return REDACTED_STR
  if (containsJwt(value)) return REDACTED_STR
  return value
}

function maybeStack(key: string): string | undefined {
  return STACK_TRACE_KEYS.has(key) ? new Error().stack : undefined
}

const DEBOUNCE_MS = 300

function makeDebouncer() {
  const pending = new Map<
    string,
    { timer: ReturnType<typeof setTimeout>; fn: () => void }
  >()

  function debounce(key: string, fn: () => void) {
    clearTimeout(pending.get(key)?.timer)
    pending.set(key, {
      fn,
      timer: setTimeout(() => {
        pending.delete(key)
        fn()
      }, DEBOUNCE_MS)
    })
  }

  function flush() {
    const callbacks = [...pending.values()]
    callbacks.forEach(({ timer }) => clearTimeout(timer))
    pending.clear()
    callbacks.forEach(({ fn }) => fn())
  }

  return { debounce, flush }
}

export function mountLocalStorageInterceptor(
  logger: LoggerService
): () => void {
  const origSetItem = localStorage.setItem.bind(localStorage)
  const origRemoveItem = localStorage.removeItem.bind(localStorage)
  const origClear = localStorage.clear.bind(localStorage)

  const { debounce, flush } = makeDebouncer()

  // A guard installed under this interceptor can drop a write (see
  // configs/i18n/lockLanguageKey). Report the attempt, not a write that
  // never landed.
  const trackRejected = (key: string, op: 'set' | 'remove') =>
    debounce(`rejected:${op}:${key}`, () =>
      logger.track(
        'localStorage.write_rejected',
        { key, op, stack: maybeStack(key) },
        'storage'
      )
    )

  localStorage.setItem = (key: string, value: string) => {
    const oldValue = localStorage.getItem(key)
    try {
      origSetItem(key, value)
    } catch (error) {
      if (isAuthKey(key))
        logger.track(
          'auth.storage_write_failed',
          {
            key,
            prev: summarizeUserStorageValue(oldValue),
            attempted: summarizeUserStorageValue(value),
            reason:
              error instanceof Error || error instanceof DOMException
                ? error.name
                : 'storage_error',
            stack: maybeStack(key)
          },
          'auth'
        )
      throw error
    }
    if (shouldFilterKey(key)) return
    if (oldValue === value) return
    if (localStorage.getItem(key) !== value) {
      if (isAuthKey(key))
        logger.track(
          'auth.storage_write_failed',
          {
            key,
            prev: summarizeUserStorageValue(oldValue),
            attempted: summarizeUserStorageValue(value),
            stored: summarizeUserStorageValue(localStorage.getItem(key)),
            reason: 'write_rejected',
            stack: maybeStack(key)
          },
          'auth'
        )
      else trackRejected(key, 'set')
      return
    }
    if (isAuthKey(key)) {
      // Fire immediately — debouncing risks dropping this on page navigation
      logger.track(
        key === LocalStorageKey.USER
          ? 'auth.user_updated'
          : 'auth.bootstrap_updated',
        {
          key,
          reason: getAuthStorageReason(),
          stack: maybeStack(key),
          prev: summarizeUserStorageValue(oldValue),
          next: summarizeUserStorageValue(value)
        },
        'auth'
      )
      return
    }
    debounce(`set:${key}`, () =>
      logger.track(
        'localStorage.set',
        {
          key,
          valueBytes: value.length,
          valueKind: value.trim().startsWith('{')
            ? 'object'
            : value.trim().startsWith('[')
              ? 'array'
              : 'scalar',
          redacted: maybeRedact(key, value) === '[REDACTED]',
          stack: maybeStack(key)
        },
        'storage'
      )
    )
  }

  localStorage.removeItem = (key: string) => {
    const oldValue = localStorage.getItem(key)
    origRemoveItem(key)
    if (shouldFilterKey(key)) return
    if (oldValue === null) return
    if (localStorage.getItem(key) !== null) {
      trackRejected(key, 'remove')
      return
    }
    if (isAuthKey(key)) {
      // Fire immediately — debouncing risks dropping this on page navigation
      logger.track(
        key === LocalStorageKey.USER
          ? 'auth.user_removed'
          : 'auth.bootstrap_removed',
        {
          key,
          prev: summarizeUserStorageValue(oldValue),
          reason: getAuthStorageReason(),
          stack: maybeStack(key)
        },
        'auth'
      )
      return
    }
    debounce(`remove:${key}`, () =>
      logger.track(
        'localStorage.remove',
        { key, stack: maybeStack(key) },
        'storage'
      )
    )
  }

  localStorage.clear = () => {
    const prev = {
      user: summarizeUserStorageValue(
        localStorage.getItem(LocalStorageKey.USER)
      ),
      bootstrap: summarizeUserStorageValue(
        localStorage.getItem(LocalStorageKey.BOOTSTRAP_TOKENS)
      )
    }
    origClear()
    if (prev.user || prev.bootstrap)
      logger.track(
        'auth.storage_cleared',
        {
          prev,
          reason: getAuthStorageReason(),
          stack: new Error().stack
        },
        'auth'
      )
    debounce('clear', () =>
      logger.track(
        'localStorage.clear',
        { stack: new Error().stack },
        'storage'
      )
    )
  }

  const onStorageEvent = (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return
    if (event.key === null) {
      logger.track(
        'auth.storage_cleared_in_other_context',
        { url: event.url },
        'auth'
      )
      debounce('cross_tab:clear', () =>
        logger.track(
          'localStorage.cross_tab_clear',
          { url: event.url },
          'storage'
        )
      )
      return
    }
    const key = event.key
    if (shouldFilterKey(key)) return
    const recordChange = () => {
      const isUser = isAuthKey(key)
      logger.track(
        'localStorage.cross_tab_change',
        {
          key,
          oldValueBytes: isUser ? undefined : event.oldValue?.length,
          newValueBytes: isUser ? undefined : event.newValue?.length,
          prev: isUser ? summarizeUserStorageValue(event.oldValue) : undefined,
          next: isUser ? summarizeUserStorageValue(event.newValue) : undefined,
          url: event.url,
          stack: maybeStack(key)
        },
        'storage'
      )
    }
    // Auth transitions must not collapse B -> A -> removal into one event.
    if (isAuthKey(key)) recordChange()
    else debounce(`cross_tab:${key}`, recordChange)
  }

  window.addEventListener('storage', onStorageEvent)

  return () => {
    flush()
    localStorage.setItem = origSetItem
    localStorage.removeItem = origRemoveItem
    localStorage.clear = origClear
    window.removeEventListener('storage', onStorageEvent)
  }
}
